/**
 * Form auto-save cadence — TRUS-498.
 *
 * Instituto de la Raza reported that a long assessment auto-saved every 30 seconds
 * while they were still composing a note, and asked for 7–10 minutes instead. The
 * 30 seconds was a literal `setInterval(..., 30000)` in FormInstanceEditor that
 * nobody could change; meanwhile the Form Builder had shown admins an "Enable
 * auto-save" checkbox since day one that wrote `settings.auto_save` and was read
 * by nothing at all.
 *
 * Auto-save now resolves through two levels:
 *
 *   1. the form template's `settings` — the org admin's default for that form;
 *   2. `user_preferences.form_autosave_*` — an individual user's override.
 *
 * At each level, "not set" means inherit from the level below it, ending at
 * DEFAULT_AUTOSAVE_SECONDS so behaviour is unchanged for anyone who configures
 * nothing. That inherit-on-null convention matches program_doc_subtypes and
 * program_field_visibility elsewhere in this codebase.
 *
 * Everything that reads or writes the cadence — the builder, the editor and the
 * user's own preference control — goes through this module, so the three cannot
 * drift apart.
 */

/** What the editor has always done, and still does when nothing is configured. */
export const DEFAULT_AUTOSAVE_SECONDS = 30;

/**
 * Bounds. These MUST match AUTOSAVE_MIN_SECONDS / AUTOSAVE_MAX_SECONDS in
 * app/routers/user_preferences.py and the CHECK constraint in migration 707 —
 * a value this module accepts but the database rejects surfaces as a 500.
 *
 * Below 15s auto-save would hammer the save endpoint on every keystroke pause;
 * above an hour it has stopped being auto-save in any meaningful sense.
 */
export const AUTOSAVE_MIN_SECONDS = 15;
export const AUTOSAVE_MAX_SECONDS = 3600;

export interface AutoSaveChoice {
  seconds: number;
  /** i18n key + English fallback, so callers stay lint-clean. */
  labelKey: string;
  labelFallback: string;
}

/**
 * Offered as a fixed list rather than a free number box. A typo in a raw input
 * ("3" instead of "300") produces a form that saves every three seconds, and the
 * person who has to notice is the caseworker, not the admin who typed it.
 */
export const AUTOSAVE_INTERVAL_CHOICES: AutoSaveChoice[] = [
  { seconds: 30, labelKey: 'forms.autosave_interval_30s', labelFallback: 'Every 30 seconds' },
  { seconds: 60, labelKey: 'forms.autosave_interval_1m', labelFallback: 'Every minute' },
  { seconds: 120, labelKey: 'forms.autosave_interval_2m', labelFallback: 'Every 2 minutes' },
  { seconds: 300, labelKey: 'forms.autosave_interval_5m', labelFallback: 'Every 5 minutes' },
  { seconds: 600, labelKey: 'forms.autosave_interval_10m', labelFallback: 'Every 10 minutes' },
  { seconds: 900, labelKey: 'forms.autosave_interval_15m', labelFallback: 'Every 15 minutes' },
  { seconds: 1800, labelKey: 'forms.autosave_interval_30m', labelFallback: 'Every 30 minutes' },
];

/** The subset of a form template's untyped `settings` bag that we care about. */
export interface FormAutoSaveSettings {
  auto_save?: boolean | null;
  /** Seconds. Named to match the key already present in seeded template settings. */
  auto_save_interval?: number | null;
}

/** A user's stored override. Either field null/undefined means "inherit". */
export interface FormAutoSavePreference {
  form_autosave_enabled?: boolean | null;
  form_autosave_interval_seconds?: number | null;
}

export interface ResolvedAutoSave {
  enabled: boolean;
  seconds: number;
  /** Where `seconds` came from, for the "your admin set this" hint in the UI. */
  source: 'user' | 'template' | 'default';
}

/**
 * Clamp to the supported range and snap to something sane.
 *
 * Values reach here from a jsonb settings bag that predates any validation, so
 * a stored `0`, a negative, a string or a NaN are all genuinely possible. Any of
 * those reaching setInterval would produce a save-per-tick loop against the API,
 * which is the one failure mode worth being paranoid about.
 */
export function normalizeAutoSaveSeconds(value: unknown): number | null {
  const seconds = typeof value === 'string' ? Number(value) : value;
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return null;
  }
  return Math.min(AUTOSAVE_MAX_SECONDS, Math.max(AUTOSAVE_MIN_SECONDS, Math.round(seconds)));
}

/**
 * Resolve the cadence for one person filling in one form.
 *
 * The user's own choice wins where they made one; otherwise the template's; and
 * failing both, the historical 30 seconds. Disabling is treated the same way — a
 * user who turns auto-save off keeps it off on a template that enables it, and a
 * user who has expressed no opinion follows the template.
 */
export function resolveAutoSave(
  templateSettings?: FormAutoSaveSettings | Record<string, any> | null,
  userPreference?: FormAutoSavePreference | null
): ResolvedAutoSave {
  const templateEnabled = templateSettings?.auto_save;
  const userEnabled = userPreference?.form_autosave_enabled;

  // `undefined` and `null` both mean "no opinion"; only a real boolean decides.
  const enabled =
    typeof userEnabled === 'boolean'
      ? userEnabled
      : typeof templateEnabled === 'boolean'
        ? templateEnabled
        : true;

  const userSeconds = normalizeAutoSaveSeconds(userPreference?.form_autosave_interval_seconds);
  if (userSeconds !== null) {
    return { enabled, seconds: userSeconds, source: 'user' };
  }

  const templateSeconds = normalizeAutoSaveSeconds(templateSettings?.auto_save_interval);
  if (templateSeconds !== null) {
    return { enabled, seconds: templateSeconds, source: 'template' };
  }

  return { enabled, seconds: DEFAULT_AUTOSAVE_SECONDS, source: 'default' };
}

/**
 * "30 seconds" / "10 minutes", for a cadence that is not one of the presets
 * above — an admin can only pick a preset, but a value seeded directly into a
 * template's settings jsonb need not be one.
 *
 * Takes the translator rather than returning English, because the caller renders
 * this inside a Spanish sentence for exactly the org that filed TRUS-498. i18next
 * picks the singular/plural form from `count`.
 */
export function describeAutoSaveInterval(
  seconds: number,
  t: (key: string, fallback: string, options?: Record<string, unknown>) => string
): string {
  if (seconds < 60) {
    return t('forms.autosave_n_seconds', '{{count}} seconds', { count: seconds });
  }
  const minutes = Math.round(seconds / 60);
  return t('forms.autosave_n_minutes', '{{count}} minutes', { count: minutes });
}
